import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
} from '@mui/material';
import { useLangFile } from 'js/context/LanguageContext';
import { PolygonDrawingContext } from '../../PolygonDrawing/PolygonDrawingContext';

export interface SplitFieldInputs {
  fieldAName: string;
  fieldANumber: string;
  fieldBName: string;
  fieldBNumber: string;
}

interface SplitFieldInputDialogProps {
  onConfirm: () => void;
}

const SplitFieldInputDialog: React.FC<SplitFieldInputDialogProps> = ({ onConfirm }) => {
  const LangFile = useLangFile();

  // Use the context properly
  const { state, dispatch } = React.useContext(PolygonDrawingContext);
  const { splitFieldInputs, showSplitFieldInputDialog } = state;

  if (!splitFieldInputs) return null;

  return (
    <Dialog
      open={showSplitFieldInputDialog}
      onClose={() =>
        dispatch({
          type: 'SET_SHOW_DIALOG',
          payload: { key: 'showSplitFieldInputDialog', value: false },
        })
      }
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{LangFile.SplitFieldInputDialog.title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Field A */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={LangFile.SplitFieldInputDialog.fieldAName}
              fullWidth
              value={splitFieldInputs.fieldAName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_SPLIT_FIELD_INPUTS',
                  payload: { key: 'fieldAName', value: e.target.value },
                })
              }
            />

            <TextField
              label={LangFile.SplitFieldInputDialog.fieldANumber}
              fullWidth
              value={splitFieldInputs.fieldANumber}
              onChange={(e) =>
                dispatch({
                  type: 'SET_SPLIT_FIELD_INPUTS',
                  payload: { key: 'fieldANumber', value: e.target.value },
                })
              }
              error={!splitFieldInputs.fieldANumber?.trim()}
            />
            <Box
              sx={{
                minHeight: '20px',
              }}
            >
              {!splitFieldInputs.fieldANumber?.trim() && (
                <Typography color="error" variant="caption">
                  {LangFile.SplitFieldInputDialog.fieldNumberRequired}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Field B */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={LangFile.SplitFieldInputDialog.fieldBName}
              fullWidth
              value={splitFieldInputs.fieldBName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_SPLIT_FIELD_INPUTS',
                  payload: { key: 'fieldBName', value: e.target.value },
                })
              }
            />

            <TextField
              label={LangFile.SplitFieldInputDialog.fieldBNumber}
              fullWidth
              value={splitFieldInputs.fieldBNumber}
              onChange={(e) =>
                dispatch({
                  type: 'SET_SPLIT_FIELD_INPUTS',
                  payload: { key: 'fieldBNumber', value: e.target.value },
                })
              }
              error={!splitFieldInputs.fieldBNumber?.trim()}
            />
            <Box
              sx={{
                minHeight: '20px',
              }}
            >
              {!splitFieldInputs.fieldBNumber?.trim() && (
                <Typography color="error" variant="caption">
                  {LangFile.SplitFieldInputDialog.fieldNumberRequired}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() =>
            dispatch({
              type: 'SET_SHOW_DIALOG',
              payload: { key: 'showSplitFieldInputDialog', value: false },
            })
          }
        >
          {LangFile.SplitFieldInputDialog.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !splitFieldInputs.fieldANumber?.trim() || !splitFieldInputs.fieldBNumber?.trim()
          }
          onClick={onConfirm}
        >
          {LangFile.SplitFieldInputDialog.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitFieldInputDialog;
